import React, { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../store";
import { useTheme } from "../../context/ThemeContext";
import Header from "./Header";
import Sidebar from "./Sidebar";

const MainLayout: React.FC = () => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isDark = theme === "dark";

  // Kullanıcı giriş yapmamışsa login sayfasına yönlendir
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, navigate]);

  // Mobile detection ve responsive behavior
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);

      if (mobile) {
        setSidebarCollapsed(false);
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Sidebar handlers
  const handleSidebarToggle = (collapsed: boolean) => {
    if (isMobile) {
      setSidebarOpen(!sidebarOpen);
    } else {
      setSidebarCollapsed(collapsed);
    }
  };

  const handleMobileMenuToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeMobileSidebar = () => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          background: isDark
            ? "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
            : "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
        }}
      >
        <div
          style={{
            textAlign: "center",
            color: isDark ? "#e2e8f0" : "#4a5568",
            padding: "2rem",
            background: isDark
              ? "rgba(30, 41, 59, 0.8)"
              : "rgba(255, 255, 255, 0.8)",
            borderRadius: "16px",
            backdropFilter: "blur(20px)",
            boxShadow: isDark
              ? "0 20px 40px rgba(0, 0, 0, 0.3)"
              : "0 20px 40px rgba(0, 0, 0, 0.1)",
          }}
        >
          <div
            style={{
              width: "48px",
              height: "48px",
              border: `3px solid ${isDark ? "#475569" : "#e2e8f0"}`,
              borderTop: `3px solid ${isDark ? "#60a5fa" : "#3b82f6"}`,
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 1rem",
            }}
          />
          <p style={{ fontSize: "1.1rem", fontWeight: "500" }}>
            Yönlendiriliyor...
          </p>
        </div>
      </div>
    );
  }

  const getContentMargin = () => {
    if (isMobile) return "0";
    return sidebarCollapsed ? "80px" : "260px";
  };

  return (
    <>
      {/* Global Styles */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          
          @keyframes slideInLeft {
            from { transform: translateX(-100%); }
            to { transform: translateX(0); }
          }
          
          @keyframes slideInRight {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
          }
          
          .main-layout {
            animation: fadeIn 0.6s ease-out;
          }
          
          .content-area {
            animation: slideInRight 0.4s ease-out;
          }
          
          /* Custom Scrollbar */
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
            height: 6px;
          }
          
          .custom-scrollbar::-webkit-scrollbar-track {
            background: ${isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)"};
            border-radius: 3px;
          }
          
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: ${isDark ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)"};
            border-radius: 3px;
            transition: background 0.3s ease;
          }
          
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: ${isDark ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.3)"};
          }
          
          /* Focus styles */
          .focus-visible {
            outline: 2px solid ${isDark ? "#60a5fa" : "#3b82f6"};
            outline-offset: 2px;
            border-radius: 8px;
          }
          
          /* Mobile optimizations */
          @media (max-width: 1023px) {
            .mobile-hidden {
              display: none !important;
            }
          }
          
          /* Print styles */
          @media print {
            .no-print {
              display: none !important;
            }
            
            .print-full-width {
              margin: 0 !important;
              padding: 1rem !important;
              background: white !important;
              box-shadow: none !important;
            }
          }
        `}
      </style>

      <div
        className="main-layout"
        style={{
          display: "flex",
          minHeight: "100vh",
          background: isDark
            ? "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
            : "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
          fontFamily:
            "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          position: "relative",
        }}
      >
        {/* Sidebar */}
        <Sidebar
          collapsed={sidebarCollapsed}
          isOpen={sidebarOpen}
          isMobile={isMobile}
          onToggle={handleSidebarToggle}
          onClose={closeMobileSidebar}
        />

        {/* Mobile Overlay */}
        {isMobile && sidebarOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.6)",
              zIndex: 1050,
              animation: "fadeIn 0.3s ease-out",
            }}
            onClick={closeMobileSidebar}
          />
        )}

        {/* Content Area */}
        <div
          className="content-area"
          style={{
            flex: 1,
            marginLeft: getContentMargin(),
            transition: "margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            display: "flex",
            flexDirection: "column",
            minHeight: "100vh",
            position: "relative",
          }}
        >
          {/* Header */}
          <Header
            onMobileMenuToggle={handleMobileMenuToggle}
            isMobile={isMobile}
            sidebarOpen={sidebarOpen}
          />

          {/* Main Content */}
          <main
            className="custom-scrollbar print-full-width"
            style={{
              flex: 1,
              padding: isMobile ? "1rem" : "1.5rem",
              background: "transparent",
              minHeight: "calc(100vh - 80px)",
              overflow: "auto",
              position: "relative",
            }}
            role="main"
          >
            {/* Content Container */}
            <div
              style={{
                maxWidth: "100%",
                margin: "0 auto",
                animation: "fadeIn 0.8s ease-out 0.2s both",
              }}
            >
              {/* Page Content */}
              <div
                style={{
                  background: isDark
                    ? "rgba(30, 41, 59, 0.6)"
                    : "rgba(255, 255, 255, 0.9)",
                  borderRadius: "20px",
                  padding: isMobile ? "1.5rem" : "2rem",
                  boxShadow: isDark
                    ? "0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05)"
                    : "0 20px 40px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.05)",
                  backdropFilter: "blur(20px)",
                  minHeight: "500px",
                  position: "relative",
                  outline: "none",
                }}
                tabIndex={-1}
              >
                <Outlet />
              </div>

              {/* Footer Space */}
              <div style={{ height: "2rem" }} />
            </div>
          </main>
        </div>

        {/* Loading Indicator */}
        <div
          id="route-loading"
          className="no-print"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            height: "3px",
            background: `linear-gradient(90deg, ${isDark ? "#60a5fa" : "#3b82f6"} 0%, ${isDark ? "#3b82f6" : "#1d4ed8"} 100%)`,
            transform: "scaleX(0)",
            transformOrigin: "left",
            transition: "transform 0.3s ease",
            zIndex: 9999,
          }}
        />
      </div>
    </>
  );
};

export default MainLayout;
